import { forwardRef } from 'react'

import { useMutation } from 'react-query'

import BlankIcon from '@/assets/icons/icon-blank.svg'
import CommentsIcon from '@/assets/icons/icon-comments.svg'
import DownloadIcon from '@/assets/icons/icon-download.svg'
import GrowthIcon from '@/assets/icons/icon-growth.svg'
import LikeIcon from '@/assets/icons/icon-like.svg'
import PlayIcon from '@/assets/icons/icon-play-arrow.svg'
import ShareIcon from '@/assets/icons/icon-reply.svg'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { getVideoSource } from '@/modules/tiktok-research/service'
import type { PostType } from '@/modules/tiktok-research/sounds/types'
import { ImageWithPlaceholder } from '@/modules/tiktok-research/ui/ImageWithPlaceholder.tsx'
import {
  getTiktokProfileURL,
  getTiktokVideoURL,
} from '@/modules/tiktok-research/utils/links'
import { handleError } from '@/utils/error'
import { formatNumberToCompact } from '@/utils/helpers'

import { getTimeDiffFromNow } from '../../utils'

interface Props {
  item: PostType
  toggleSelect: (videoId: PostType['id']) => void
  isSelected: boolean
}

export const VideoCard = forwardRef<HTMLDivElement, Props>(
  ({ item, toggleSelect, isSelected }, ref) => {
    const getVideoSourceMutation = useMutation(() => getVideoSource(item.id), {
      onError: (error) =>
        handleError(error, 'Something went wrong getting video source'),
      onSuccess: (src) => {
        window.open(src, '_blank')
      },
    })

    const handleCheckboxChange = () => {
      toggleSelect(item.id)
    }
    const handleOpenProfileInNewTab = () => {
      window.open(getTiktokProfileURL(item.accountName))
    }

    return (
      <div
        ref={ref}
        className="relative h-[450px] w-full overflow-hidden rounded transition-all"
      >
        {item.isAdult && (
          <span className="bg-warning-light text-title absolute left-1/2 top-2 -translate-x-2/4 rounded-md px-2 py-1">
            Adult only
          </span>
        )}
        {/* Image */}
        <a
          href={getTiktokVideoURL(item.accountName, item.postId)}
          target="_blank"
          rel="noopener noreferrer"
          key={item.id}
        >
          <ImageWithPlaceholder
            className="h-full w-full object-cover"
            src={item.coverPhoto}
            alt="TikTok Video"
          />
        </a>

        {/* Header */}
        <div className="absolute left-0 right-0 top-0 space-x-2 rounded-b-md bg-gradient-to-t from-black/40 to-black/70 p-3 pt-4">
          <div className="flex max-w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full">
                <ImageWithPlaceholder
                  src={item.accountAvatar}
                  alt="Avatar"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="overflow-hidden text-ellipsis text-sm font-medium leading-tight text-white">
                @{item.accountName}
              </div>
              <BlankIcon
                onClick={handleOpenProfileInNewTab}
                className="shrink-0 cursor-pointer"
              />
            </div>
            <div className="flex gap-4">
              <Button
                className="h-7 w-7 rounded-full bg-black bg-opacity-30 p-1 hover:bg-black hover:bg-opacity-60"
                onClick={() => getVideoSourceMutation.mutate()}
                disabled={getVideoSourceMutation.isLoading}
              >
                <DownloadIcon />
              </Button>
              <label className="flex h-7 w-7 cursor-pointer items-center justify-center gap-2 rounded-full bg-black bg-opacity-30">
                <Checkbox
                  checked={isSelected}
                  className="border bg-white"
                  onCheckedChange={handleCheckboxChange}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Growth */}
        <div className="absolute left-3 top-16 inline-flex flex-col items-start justify-center gap-2 rounded-md bg-black bg-opacity-40 p-2">
          <div className="inline-flex items-center justify-start gap-0.5 rounded">
            <div className="text-xs font-semibold leading-none text-white">
              {Number(item.percentage).toFixed(2)}%
            </div>
            <GrowthIcon className="fill-white" />
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 space-y-2 rounded-t-md bg-gradient-to-b from-black/40 to-black/70 px-3 py-4">
          <div className="text-neutral-default line-clamp-1 overflow-hidden text-xs font-medium leading-[14px]">
            {item.description}
          </div>
          <div className="flex max-w-full gap-2">
            <div className="text-neutral-default flex max-w-[calc(25%-6px)] items-center gap-0.5 text-sm font-medium">
              <PlayIcon className="shrink-0 fill-white" />
              <span>{formatNumberToCompact(item.playCount)}</span>
            </div>
            <div className="text-neutral-default flex max-w-[calc(25%-6px)] items-center gap-1 text-sm font-medium">
              <LikeIcon className="shrink-0" />
              <span>{formatNumberToCompact(item.likeCount)}</span>
            </div>
            <div className="text-neutral-default flex max-w-[calc(25%-6px)] items-center gap-1 text-sm font-medium">
              <CommentsIcon className="shrink-0" />
              {formatNumberToCompact(item.commentCount)}
            </div>
            <div className="text-neutral-default flex max-w-[calc(25%-6px)] items-center gap-1 overflow-hidden text-ellipsis text-sm font-medium">
              <ShareIcon className="shrink-0" />
              {formatNumberToCompact(item.shareCount)}
            </div>
          </div>
          <div className="text-secondary-100 text-sm">
            {getTimeDiffFromNow(new Date(item.postCreatedAt))}
          </div>
        </div>
      </div>
    )
  },
)

VideoCard.displayName = 'VideoCard'
